import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import DOMPurify from 'dompurify';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Register custom fonts with Quill 2.0 using STYLE attributor (inline styles for email compatibility)
const FontStyle = Quill.import('attributors/style/font');
FontStyle.whitelist = [
  'Monotype Corsiva, Dancing Script, cursive',
  'Dancing Script, cursive',
  'Great Vibes, cursive',
  'Parisienne, cursive',
  'Sacramento, cursive',
  'Playfair Display, serif',
  'Georgia, Times New Roman, serif',
  'Times New Roman, Times, serif',
  'Arial, Helvetica, sans-serif',
  'Courier New, Courier, monospace'
];
Quill.register(FontStyle, true);

// Register font size as STYLE attributor (inline font-size for email compatibility)
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'];
Quill.register(SizeStyle, true);
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, rectSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  FileText, Plus, Edit2, Trash2, Copy, Wand2, Building2, ChevronRight, ChevronDown,
  Image, Upload, X, Eye, ImagePlus, Check, GripVertical, Film, Link2, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import { formatSignature } from '../utils/signatureFormatter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SortableTemplateCard({ tpl, onPreview, onEdit, onDuplicate, onDelete, onRestore, onPermanentDelete, LEAD_TYPE_COLORS, LEAD_TYPE_LABELS }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tpl.template_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : tpl.deleted ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <Card className={`bg-[#111] border-[#262626] transition-all ${tpl.deleted ? 'border-red-500/20 bg-red-500/5' : ''} ${isDragging ? 'border-red-600/50 shadow-lg shadow-red-600/10 ring-1 ring-red-600/20' : 'hover:border-[#404040]'}`} data-testid={`template-card-${tpl.template_id}`}>
        <div className="p-4">
          {!tpl.deleted && (
          <div {...listeners} className="flex items-center justify-center mb-2 cursor-grab active:cursor-grabbing opacity-30 hover:opacity-70 transition-opacity" data-testid={`drag-handle-${tpl.template_id}`}>
            <GripVertical className="w-4 h-4 text-slate-500" />
          </div>
          )}
          {tpl.deleted && (
            <div className="text-center mb-2">
              <Badge className="bg-red-500/10 text-red-400 border border-red-500/20 text-[9px] font-bold tracking-wider uppercase">Deleted</Badge>
            </div>
          )}
          {tpl.logo_url && (
            <div className="text-center mb-3 pb-3 border-b border-[#1A1A1A]">
              <img src={tpl.logo_url} alt="" className="max-h-10 inline-block object-contain" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
            </div>
          )}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h4 className={`font-semibold text-sm truncate ${tpl.deleted ? 'text-slate-500 line-through' : 'text-white'}`}>{tpl.name}</h4>
              <Badge className={`mt-1 text-[9px] font-bold tracking-wider uppercase border ${LEAD_TYPE_COLORS[tpl.lead_type]}`}>
                {LEAD_TYPE_LABELS[tpl.lead_type]}
              </Badge>
            </div>
          </div>
          {tpl.deleted ? (
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => onRestore(tpl.template_id)} className="flex-1 border-green-500/30 bg-transparent hover:bg-green-500/10 text-green-400 text-[10px] font-bold tracking-wider uppercase h-8">
                <RotateCcw className="w-3 h-3 mr-1" /> Restore
              </Button>
              <Button variant="outline" size="sm" onClick={() => onPermanentDelete(tpl.template_id)} className="border-red-500/30 bg-transparent hover:bg-red-500/10 text-red-400 h-8 px-2" title="Delete permanently">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ) : (
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => onPreview(tpl)} className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 h-8 px-2" title="Quick Preview" data-testid={`tpl-quick-preview-${tpl.template_id}`}>
              <Eye className="w-3 h-3" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onEdit(tpl)} className="flex-1 border-[#333] bg-transparent hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase h-8">
              <Edit2 className="w-3 h-3 mr-1" /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDuplicate(tpl)} className="border-[#333] bg-transparent hover:bg-[#1a1a1a] text-slate-400 h-8 px-2" title="Duplicate">
              <Copy className="w-3 h-3" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => onDelete(tpl.template_id)} className="border-red-500/30 bg-transparent hover:bg-red-500/10 text-red-400 h-8 px-2" title="Delete">
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
          )}
        </div>
      </Card>
    </div>
  );
}



const LEAD_TYPE_LABELS = {
  new_vehicle_inquiry: 'New Vehicle',
  new_vehicle_inquiry_vin: 'New Vehicle (VIN)',
  used_vehicle_inquiry: 'Used Vehicle',
  used_vehicle_inquiry_cpo: 'Used Vehicle (CPO)',
  general_inquiry: 'General Inquiry',
  service_appointment: 'Service',
  onstar_service: 'OnStar Service',
  parts_inquiry: 'Parts',
};

const LEAD_TYPE_COLORS = {
  new_vehicle_inquiry: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  new_vehicle_inquiry_vin: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  used_vehicle_inquiry: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  used_vehicle_inquiry_cpo: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  general_inquiry: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  service_appointment: 'bg-green-500/10 text-green-400 border-green-500/20',
  onstar_service: 'bg-teal-500/10 text-teal-400 border-teal-500/20',
  parts_inquiry: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const TEMPLATE_VARS = [
  { key: '{{customer_name}}', label: 'Customer Name' },
  { key: '{{vehicle_year}}', label: 'Vehicle Year' },
  { key: '{{vehicle_make}}', label: 'Vehicle Make' },
  { key: '{{vehicle_model}}', label: 'Vehicle Model' },
  { key: '{{vehicle_trim}}', label: 'Vehicle Trim' },
  { key: '{{vehicle_color}}', label: 'Vehicle Color' },
  { key: '{{dealership_name}}', label: 'Dealership Name' },
  { key: '{{dealership_phone}}', label: 'Dealership Phone' },
  { key: '{{dealership_address}}', label: 'Dealership Address' },
  { key: '{{dealership_website}}', label: 'Website' },
  { key: '{{hours_directions}}', label: 'Hours & Directions' },
  { key: '{{trade_in_vehicle}}', label: 'Trade-In Vehicle' },
  { key: '{{budget_range}}', label: 'Budget Range' },
  { key: '{{vin}}', label: 'VIN' },
  { key: '{{vehicle_images}}', label: 'Vehicle Images (Repo)' },
  { key: '{{vehicle_direct}}', label: 'Vehicle Photos (VDP)' },
  { key: '{{vehicle_buy}}', label: 'View & Buy Online' },
  { key: '{{vehicle_testdrive}}', label: 'Test Drive Button' },
  { key: '{{inventory_testdrive}}', label: 'Inventory + Test Drive' },
  { key: '{{preowned_specials}}', label: 'Pre-Owned Specials Button' },
  { key: '{{vehicle_cta_buttons}}', label: 'Buy + Test Drive Buttons' },
  { key: '{{deal_testdrive_buttons}}', label: 'Deal + Test Drive Buttons' },
  { key: '{{testdrive_preowned_buttons}}', label: 'Test Drive + Pre-Owned Specials' },
  { key: '{{recommended_vehicles}}', label: 'Recommended Vehicles (3 cards)' },
  { key: '{{similar_new_vehicles}}', label: 'Similar New Vehicles (3 cards)' },
  { key: '{{similar_preowned_vehicles}}', label: 'Similar Pre-Owned Vehicles (3 cards)' },
  { key: '{{new_vehicle_specials}}', label: 'New Vehicle Specials (3 cards)' },
  { key: '{{search_new_vehicles}}', label: 'Search New Vehicles Button' },
  { key: '{{search_preowned_vehicles}}', label: 'Search Pre-Owned Vehicles Button' },
  { key: '{{create_deal}}', label: 'Create Your Deal' },
  { key: '{{vehicle_link}}', label: 'Vehicle Link' },
  { key: '{{carfax_link}}', label: 'CarFax Report' },
  { key: '{{vehicle_price}}', label: 'Vehicle Price' },
  { key: '{{ai_response}}', label: 'AI Response' },
  { key: '{{email_buttons}}', label: 'Email Buttons' },
];

const SAMPLE_DATA = {
  customer_name: 'John Smith',
  vehicle_year: '2026',
  vehicle_make: 'Ford',
  vehicle_model: 'F-150',
  vehicle_trim: 'Lariat',
  vehicle_color: 'Oxford White',
  dealership_name: 'Demo Motors',
  dealership_phone: '(555) 123-4567',
  dealership_address: '123 Auto Drive, Tampa, FL 33601',
  dealership_website: 'www.demomotors.com',
  trade_in_vehicle: '2020 Chevrolet Silverado',
  budget_range: '$45,000 - $55,000',
  vin: '1GC5KME79TF215021',
};

const quillFormats = [
  'header', 'font', 'size', 'bold', 'italic', 'underline', 'strike',
  'color', 'background', 'align',
  'list', 'indent', 'blockquote',
  'link', 'image'
];

// CRMs that use auto-generated subject lines
const AUTO_SUBJECT_CRMS = ['elead', 'ford_direct'];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedDealership, setExpandedDealership] = useState(null);
  const [templateAliasFilter, setTemplateAliasFilter] = useState('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [seeding, setSeedingId] = useState(null);
  const [form, setForm] = useState({
    name: '', lead_type: 'new_vehicle_inquiry', subject: '', body: '', dealership_id: '', logo_url: '', alias_email: '',
    show_window_sticker: false, show_warranty_btn: false, show_similar_vehicles_btn: false, show_preowned_btn: false, show_new_inventory_btn: false,
    show_test_drive_btn: false,
    show_cpo_benefits: false, show_cpo_vs_used: false, show_cpo_inspection: false,
    website_override: ''
  });
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showLogoLibrary, setShowLogoLibrary] = useState(false);
  const [logoLibrary, setLogoLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [quickPreview, setQuickPreview] = useState(null);
  const [showImageInsert, setShowImageInsert] = useState(false);
  const [imageInsertForm, setImageInsertForm] = useState({ url: '', linkUrl: '', altText: '', width: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const quillRef = useRef(null);
  const lastCursorPos = useRef(null);
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Custom link handler: when removing a link, also strip bold/underline/color
  const linkHandler = useCallback(function(value) {
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    if (value) {
      // Adding a link - use Quill's default prompt
      const range = quill.getSelection();
      if (range) {
        const url = prompt('Enter URL:');
        if (url) {
          quill.format('link', url);
        }
      }
    } else {
      // Removing a link - also strip formatting added to mimic link appearance
      const range = quill.getSelection();
      if (range && range.length > 0) {
        quill.format('link', false);
        quill.formatText(range.index, range.length, {
          'bold': false,
          'underline': false,
          'color': false
        });
      } else if (range) {
        // Cursor is in a link but no selection - find link bounds
        const [leaf] = quill.getLeaf(range.index);
        if (leaf && leaf.parent) {
          const blot = leaf.parent;
          const linkNode = blot.domNode?.closest?.('a') || (blot.domNode?.tagName === 'A' ? blot.domNode : null);
          if (linkNode) {
            const idx = quill.getIndex(blot);
            const len = blot.length();
            quill.formatText(idx, len, {
              'link': false,
              'bold': false,
              'underline': false,
              'color': false
            });
          }
        }
      }
    }
  }, []);

  const quillModulesWithHandler = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        [{ 'font': [
          false,
          'Monotype Corsiva, Dancing Script, cursive',
          'Dancing Script, cursive',
          'Great Vibes, cursive',
          'Parisienne, cursive',
          'Sacramento, cursive',
          'Playfair Display, serif',
          'Georgia, Times New Roman, serif',
          'Times New Roman, Times, serif',
          'Arial, Helvetica, sans-serif',
          'Courier New, Courier, monospace'
        ] }],
        [{ 'size': ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        ['blockquote'],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        link: linkHandler
      }
    }
  }), [linkHandler]);

  useEffect(() => {
    const init = async () => {
      try {
        const [tplRes, dlrRes] = await Promise.all([
          axios.get(`${API}/templates`, { headers, withCredentials: true }),
          axios.get(`${API}/dealerships`, { headers, withCredentials: true })
        ]);
        setTemplates(tplRes.data);
        setDealerships(dlrRes.data);
      } catch (err) { console.error('Failed to load templates/dealerships:', err); }
      setLoading(false);
    };
    init();
  }, []);

  const fetchTemplates = async (includeDeleted) => {
    try {
      const params = includeDeleted ? '?include_deleted=true' : '';
      const res = await axios.get(`${API}/templates${params}`, { headers, withCredentials: true });
      setTemplates(res.data);
    } catch (err) { console.error('Failed to fetch templates:', err); }
  };

  const fetchLogoLibrary = async () => {
    try {
      const res = await axios.get(`${API}/upload/logos`, { headers, withCredentials: true });
      setLogoLibrary(res.data);
    } catch (err) { console.error('Failed to fetch logo library:', err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/templates/${editing}`, form, { headers, withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/templates`, form, { headers, withCredentials: true });
        toast.success('Template created');
      }
      setShowEditor(false);
      setEditing(null);
      setForm({ name: '', lead_type: 'new_vehicle_inquiry', subject: '', body: '', dealership_id: '', logo_url: '',
        show_window_sticker: false, show_warranty_btn: false, show_similar_vehicles_btn: false, show_preowned_btn: false, show_new_inventory_btn: false,
        show_test_drive_btn: false,
        show_cpo_benefits: false, show_cpo_vs_used: false, show_cpo_inspection: false, website_override: '' });
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save template');
    }
    setSaving(false);
  };

  const handleEdit = (tpl) => {
    setEditing(tpl.template_id);
    setForm({
      name: tpl.name || '',
      lead_type: tpl.lead_type || 'new_vehicle_inquiry',
      subject: tpl.subject || '',
      body: tpl.body || '',
      dealership_id: tpl.dealership_id || '',
      logo_url: tpl.logo_url || '',
      show_window_sticker: tpl.show_window_sticker || false,
      show_new_inventory_btn: tpl.show_new_inventory_btn || false,
      show_warranty_btn: tpl.show_warranty_btn || false,
      show_similar_vehicles_btn: tpl.show_similar_vehicles_btn || false,
      show_preowned_btn: tpl.show_preowned_btn || false,
      show_test_drive_btn: tpl.show_test_drive_btn || false,
      show_cpo_benefits: tpl.show_cpo_benefits || false,
      show_cpo_vs_used: tpl.show_cpo_vs_used || false,
      show_cpo_inspection: tpl.show_cpo_inspection || false,
      website_override: tpl.website_override || '',
      alias_email: tpl.alias_email || ''
    });
    setShowPreview(false);
    setShowLogoLibrary(false);
    setShowEditor(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/templates/${id}`, { headers, withCredentials: true });
      toast.success('Template deleted');
      fetchTemplates(showDeleted);
    } catch (err) { console.error('Failed to delete template:', err); toast.error('Failed to delete template'); }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/templates/${id}/restore`, {}, { headers, withCredentials: true });
      toast.success('Template restored');
      fetchTemplates(showDeleted);
    } catch (err) { console.error('Failed to restore template:', err); toast.error('Failed to restore template'); }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm('Permanently delete this template? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/templates/${id}/permanent`, { headers, withCredentials: true });
      toast.success('Template permanently deleted');
      fetchTemplates(showDeleted);
    } catch (err) { console.error('Failed to permanently delete:', err); }
  };

  const handleDuplicate = async (tpl) => {
    try {
      await axios.post(`${API}/templates`, {
        ...tpl,
        name: `${tpl.name} (Copy)`,
        template_id: undefined,
        is_default: false
      }, { headers, withCredentials: true });
      toast.success('Template duplicated');
      fetchTemplates();
    } catch (err) { console.error('Failed to duplicate template:', err); toast.error('Failed to duplicate template'); }
  };

  const openNew = (dealershipId = '') => {
    setEditing(null);
    setForm({ name: '', lead_type: 'new_vehicle_inquiry', subject: '', body: '', dealership_id: dealershipId, logo_url: '',
      show_window_sticker: false, show_warranty_btn: false, show_similar_vehicles_btn: false, show_preowned_btn: false, show_new_inventory_btn: false,
      show_test_drive_btn: false,
      show_cpo_benefits: false, show_cpo_vs_used: false, show_cpo_inspection: false, website_override: '' });
    setShowPreview(false);
    setShowLogoLibrary(false);
    setShowEditor(true);
  };

  const seedDefaults = async (dealershipId) => {
    setSeedingId(dealershipId);
    try {
      await axios.post(`${API}/templates/seed-defaults/${dealershipId}`, {}, { headers, withCredentials: true });
      toast.success('Default templates created');
      fetchTemplates();
    } catch (err) { console.error('Failed to seed defaults:', err); toast.error('Failed to seed defaults'); }
    setSeedingId(null);
  };

  const insertVariable = (varKey) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor?.();
      if (quill) {
        const range = quill.getSelection() || { index: quill.getLength() - 1 };
        quill.insertText(range.index, varKey);
        quill.setSelection(range.index + varKey.length);
      }
    } else {
      setForm(p => ({ ...p, body: p.body + varKey }));
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File too large. Max 2MB');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/logo`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setForm(p => ({ ...p, logo_url: res.data.logo_url }));
      toast.success('Logo uploaded');
      fetchLogoLibrary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  const handleDeleteLogo = async (logoId) => {
    try {
      await axios.delete(`${API}/upload/logos/${logoId}`, { headers, withCredentials: true });
      toast.success('Logo removed');
      fetchLogoLibrary();
      // Clear selection if the deleted logo was selected
      const deleted = logoLibrary.find(l => l.logo_id === logoId);
      if (deleted && form.logo_url === deleted.url) {
        setForm(p => ({ ...p, logo_url: '' }));
      }
    } catch (err) { console.error('Failed to delete logo:', err); toast.error('Failed to delete logo'); }
  };

  const handleImageUploadForBody = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Max 10MB');
      return;
    }
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        withCredentials: true
      });
      setImageInsertForm(p => ({ ...p, url: res.data.image_url }));
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploadingImage(false);
  };

  const insertImageIntoBody = () => {
    const { url, linkUrl, altText, width } = imageInsertForm;
    if (!url) { toast.error('Please provide an image URL or upload an image'); return; }
    const alt = altText || 'Image';
    const style = width ? `max-width:${width};height:auto;` : 'max-width:100%;height:auto;';
    let imgTag = `<img src="${url}" alt="${alt}" style="${style}border-radius:4px" />`;
    if (linkUrl) {
      imgTag = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer">${imgTag}</a>`;
    }
    const html = `<div style="text-align:center;margin:12px 0">${imgTag}</div>`;

    const quill = quillRef.current?.getEditor?.();
    if (quill) {
      const range = lastCursorPos.current || quill.getSelection() || { index: quill.getLength() - 1 };
      quill.clipboard.dangerouslyPasteHTML(range.index, html);
      lastCursorPos.current = null;
    } else {
      setForm(p => ({ ...p, body: p.body + html }));
    }
    setShowImageInsert(false);
    setImageInsertForm({ url: '', linkUrl: '', altText: '', width: '' });
    toast.success('Image inserted');
  };

  const getTemplatesForDealership = (dealershipId) => {
    return templates
      .filter(t => t.dealership_id === dealershipId)
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
  };

  const getFilteredTemplates = (dealershipTemplates) => {
    if (activeTab === 'all') return dealershipTemplates;
    return dealershipTemplates.filter(t => t.lead_type === activeTab);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Find which dealership these templates belong to
    const activeTpl = templates.find(t => t.template_id === active.id);
    if (!activeTpl) return;
    const dealershipId = activeTpl.dealership_id;
    const dlrTemplates = getTemplatesForDealership(dealershipId);
    const filtered = getFilteredTemplates(dlrTemplates);

    const oldIndex = filtered.findIndex(t => t.template_id === active.id);
    const newIndex = filtered.findIndex(t => t.template_id === over.id);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const movedItem = filtered[oldIndex];
    const fullList = [...dlrTemplates];
    const fromFull = fullList.findIndex(t => t.template_id === movedItem.template_id);
    const targetItem = filtered[newIndex];
    let toFull = fullList.findIndex(t => t.template_id === targetItem.template_id);

    fullList.splice(fromFull, 1);
    fullList.splice(toFull, 0, movedItem);

    const newOrder = fullList.map(t => t.template_id);
    setTemplates(prev => {
      const updated = [...prev];
      for (let i = 0; i < newOrder.length; i++) {
        const idx = updated.findIndex(t => t.template_id === newOrder[i]);
        if (idx !== -1) updated[idx] = { ...updated[idx], sort_order: i };
      }
      return updated;
    });

    try {
      await axios.post(`${API}/templates/reorder/${dealershipId}`, { order: newOrder }, { headers, withCredentials: true });
    } catch {
      toast.error('Failed to save template order');
      fetchTemplates();
    }
  }, [templates, activeTab, headers]);

  const isAutoSubjectCRM = () => {
    const dlr = dealerships.find(d => d.dealership_id === form.dealership_id);
    return dlr ? AUTO_SUBJECT_CRMS.includes(dlr.crm) : false;
  };

  const getSelectedCRMLabel = () => {
    const dlr = dealerships.find(d => d.dealership_id === form.dealership_id);
    if (!dlr) return '';
    if (dlr.crm === 'elead') return 'Elead';
    if (dlr.crm === 'ford_direct') return 'Ford Direct';
    return dlr.crm || '';
  };

  // Render preview with sample data (for editor preview and quick preview)
  const renderPreviewForBody = (body, subject, logoUrl, dealershipId, buttonToggles = null) => {
    // Strip &nbsp; entities for clean rendering
    body = (body || '').replace(/&nbsp;/g, ' ');
    subject = (subject || '').replace(/&nbsp;/g, ' ');

    // Format signature FIRST (uses empty <p></p> as group boundaries)
    body = formatSignature(body);

    // Then remove leftover empty paragraphs from the body
    body = body.replace(/<p><\/p>/g, '').replace(/<p><br><\/p>/g, '');

    // Replace variables with sample data
    Object.entries(SAMPLE_DATA).forEach(([key, value]) => {
      const re = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      body = body.replace(re, value);
      subject = subject.replace(re, value);
    });

    // Replace {{logo}} with centered logo image
    if (logoUrl) {
      body = body.replace(/\{\{logo\}\}/g, `<div style="text-align:center;margin:16px 0"><img src="${logoUrl}" alt="Logo" style="max-height:80px;display:inline-block" /></div>`);
    } else {
      body = body.replace(/\{\{logo\}\}/g, '');
    }

    // Replace {{vehicle_images}} with repo image placeholder
    body = body.replace(/\{\{vehicle_images\}\}/g, 
      '<div style="text-align:center;margin:16px 0;padding:20px;background:#f0f0f0;border-radius:8px;border:2px dashed #ccc">' +
      '<p style="color:#666;font-weight:bold;margin:0">Repository Vehicle Image</p>' +
      '<p style="color:#888;font-size:11px;margin:4px 0 0 0">(Generic model photo from repo)</p>' +
      '</div>'
    );

    // Replace {{vehicle_direct}} with VDP photos placeholder
    body = body.replace(/\{\{vehicle_direct\}\}/g, 
      '<div style="text-align:center;margin:16px 0;padding:20px;background:#e8f5e9;border-radius:8px;border:2px dashed #4caf50">' +
      '<p style="color:#2e7d32;font-weight:bold;margin:0">Actual Vehicle Photos (VDP)</p>' +
      '<p style="color:#888;font-size:11px;margin:4px 0 0 0">(Up to 3 photos from dealer website)</p>' +
      '</div>'
    );

    // Replace {{vehicle_buy}} with View & Buy button placeholder
    body = body.replace(/\{\{vehicle_buy\}\}/g, 
      '<div style="text-align:center;margin:16px 0">' +
      '<span style="display:inline-block;padding:16px 48px;background-color:#f59e0b;color:#ffffff;font-size:18px;font-weight:bold;border-radius:8px;font-family:Arial,sans-serif">' +
      'View &amp; Buy Online' +
      '</span></div>'
    );

    // Replace {{create_deal}} with Create Your Deal placeholder
    body = body.replace(/\{\{create_deal\}\}/g, 
      '<div style="text-align:center;margin:16px 0;padding:12px;background:#f5f5f5;border-radius:8px;border:2px dashed #666">' +
      '<p style="color:#333;font-weight:bold;margin:0">Create Your Deal Button</p>' +
      '<p style="color:#888;font-size:11px;margin:4px 0 0 0">(Links to express checkout for this VIN)</p>' +
      '</div>'
    );

    // Replace {{vehicle_testdrive}} with Schedule Test Drive placeholder
    body = body.replace(/\{\{vehicle_testdrive\}\}/g, 
      '<div style="text-align:center;margin:16px 0">' +
      '<span style="display:inline-block;padding:16px 48px;background-color:#2563eb;color:#ffffff;font-size:18px;font-weight:bold;border-radius:8px;font-family:Arial,sans-serif">' +
      'Schedule Test Drive' +
      '</span></div>'
    );

    // Replace {{vehicle_cta_buttons}} with side-by-side Buy + Test Drive placeholder
    body = body.replace(/\{\{vehicle_cta_buttons\}\}/g, 
      '<div style="text-align:center;margin:16px 0">' +
      '<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto"><tr>' +
      '<td style="background-color:#f59e0b;border-radius:8px;padding:16px 32px;text-align:center">' +
      '<span style="color:#ffffff;font-size:16px;font-weight:bold;font-family:Arial,sans-serif">View &amp; Buy Online</span></td>' +
      '<td style="width:16px"></td>' +
      '<td style="background-color:#2563eb;border-radius:8px;padding:16px 32px;text-align:center">' +
      '<span style="color:#ffffff;font-size:16px;font-weight:bold;font-family:Arial,sans-serif">Schedule Test Drive</span></td>' +
      '</tr></table></div>'
    );

    // Replace {{deal_testdrive_buttons}} with side-by-side Create Your Deal + Schedule Test Drive placeholder
    body = body.replace(/\{\{deal_testdrive_buttons\}\}/g, 
      '<div style="text-align:center;margin:16px 0">' +
      '<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto"><tr>' +
      '<td style="background-color:#1a1a1a;border-radius:8px;padding:16px 32px;text-align:center">' +
      '<span style="color:#ffffff;font-size:16px;font-weight:bold;font-family:Arial,sans-serif">Create Your Deal</span></td>' +
      '<td style="width:16px"></td>' +
      '<td style="background-color:#2563eb;border-radius:8px;padding:16px 32px;text-align:center">' +
      '<span style="color:#ffffff;font-size:16px;font-weight:bold;font-family:Arial,sans-serif">Schedule Test Drive</span></td>' +
      '</tr></table></div>'
    );

    // Replace {{inventory_testdrive}} with side-by-side {Model} Inventory + Schedule Test Drive placeholder
    body = body.replace(/\{\{inventory_testdrive\}\}/g, 
      '<div style="text-align:center;margin:16px 0">' +
      '<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center" style="margin:0 auto"><tr>' +
      '<td style="background-color:#1a1a1a;border-radius:8px;padding:16px 32px;text-align:center">' +
      '<span style="color:#ffffff;font-size:16px;font-weight:bold;font-family:Arial,sans-serif">{Model} Inventory</span></td>' +
      '<td style="width:16px"></td>' +
      '<td style="background-color:#2563eb;border-radius:8px;padding:16px 32px;text-align:center">' +
      '<span style="color:#ffffff;font-size:16px;font-weight:bold;font-family:Arial,sans-serif">Schedule Test Drive</span></td>' +
      '</tr></table></div>'
    );


    // Replace {{vehicle_link}} with vehicle link placeholder
    body = body.replace(/\{\{vehicle_link\}\}/g, 
      '<div style="text-align:center;margin:16px 0;padding:12px;background:#fff3e0;border-radius:8px;border:2px dashed #ff9800">' +
      '<p style="color:#e65100;font-weight:bold;margin:0">View Vehicle on Website Link</p>' +
      '<p style="color:#888;font-size:11px;margin:4px 0 0 0">(VIN search link to dealer site)</p>' +
      '</div>'
    );

    // Replace {{carfax_link}} with CarFax badge placeholder
    body = body.replace(/\{\{carfax_link\}\}/g, 
      '<div style="text-align:center;margin:16px 0;padding:12px;background:#f0f8ff;border-radius:8px;border:2px dashed #4a90d9">' +
      '<p style="color:#2b6cb0;font-weight:bold;margin:0">CARFAX Report Badge</p>' +
      '<p style="color:#888;font-size:11px;margin:4px 0 0 0">(Shown for used vehicles with VIN)</p>' +
      '</div>'
    );

    // Replace {{vehicle_price}} with price placeholder
    body = body.replace(/\{\{vehicle_price\}\}/g, 
      '<span style="color:#16a34a;font-weight:bold">$XX,XXX</span>'
    );

    // Replace {{ai_response}} with AI response placeholder
    body = body.replace(/\{\{ai_response\}\}/g, 
      '<span style="color:#7c3aed;font-style:italic;background:#f3f0ff;padding:2px 6px;border-radius:4px">' +
      'AI-generated personalized intro based on lead context' +
      '</span>'
    );

    const dlr = dealerships.find(d => d.dealership_id === dealershipId);
    const isAutoSubject = dlr ? AUTO_SUBJECT_CRMS.includes(dlr.crm) : false;
    if (isAutoSubject) {
      subject = 'Re: Internet Lead - 2026 Ford F-150 Lariat';
    }

    // Append button previews based on toggles
    if (buttonToggles) {
      const tdStyle = 'background-color:#2563eb;border-radius:6px;padding:12px 24px;text-align:center;';
      const aStyle = 'text-decoration:none !important;border:none;display:block;';
      const fontAttr = 'color="#ffffff" style="font-size:14px;font-weight:bold;font-family:Arial,sans-serif;text-decoration:none;color:#ffffff"';
      const makeBtn = (label) => `<td style="${tdStyle}"><a href="#" style="${aStyle}"><font ${fontAttr}>${label}</font></a></td><td style="width:12px"></td>`;
      const buttons = [];
      if (buttonToggles.show_test_drive_btn) buttons.push(makeBtn('Schedule Test Drive'));
      if (buttonToggles.show_new_inventory_btn) buttons.push(makeBtn('New Vehicle Inventory'));
      if (buttonToggles.show_window_sticker) buttons.push(makeBtn('Window Sticker'));
      if (buttonToggles.show_warranty_btn) buttons.push(makeBtn('New Vehicle Warranty'));
      if (buttonToggles.show_preowned_btn) buttons.push(makeBtn('Pre-Owned Vehicles'));
      if (buttonToggles.show_cpo_benefits) buttons.push(makeBtn('CPO Benefits'));
      if (buttonToggles.show_cpo_vs_used) buttons.push(makeBtn('CPO vs. Used'));
      if (buttonToggles.show_cpo_inspection) buttons.push(makeBtn('CPO Inspection Form'));
      const buttonsHtml = buttons.length > 0
        ? `<div style="text-align:center;margin:20px 0;padding:16px 0"><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center"><tr>${buttons.join('')}</tr></table></div>`
        : '';
      if (body.includes('{{email_buttons}}')) {
        body = body.replace(/\{\{email_buttons\}\}/g, buttonsHtml);
      } else if (buttonsHtml) {
        body += buttonsHtml;
      }
    }

    return { subject, body, dealershipName: dlr?.name || 'Demo Motors' };
  };

  // Render preview for the editor form
  const renderPreview = () => {
    return renderPreviewForBody(form.body, form.subject, form.logo_url, form.dealership_id, form);
  };

  // Render quick preview for a template card
  const renderQuickPreviewData = (tpl) => {
    return renderPreviewForBody(tpl.body, tpl.subject, tpl.logo_url, tpl.dealership_id, tpl);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const preview = renderPreview();

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">
            Template Library
          </h1>
          <p className="text-slate-400 text-sm mt-2">{templates.length} templates across {dealerships.length} dealerships</p>
        </div>
        <Dialog open={showEditor} onOpenChange={(open) => { setShowEditor(open); if (!open) { setShowPreview(false); setShowLogoLibrary(false); } }}>
          <DialogTrigger asChild>
            <Button onClick={() => openNew()} className="bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase hover:shadow-[0_0_15px_rgba(220,38,38,0.4)] transition-all" data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
                {showPreview ? 'Preview Template' : editing ? 'Edit Template' : 'Create Template'}
              </DialogTitle>
            </DialogHeader>
            {!showPreview && (
              <div className="flex justify-end -mt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(true)}
                  className="border-[#333] text-xs font-bold tracking-wider uppercase text-slate-400"
                  data-testid="tpl-preview-btn"
                >
                  <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
                </Button>
              </div>
            )}

            {showPreview ? (
              /* ==================== PREVIEW MODE ==================== */
              <div className="mt-2 space-y-4" data-testid="tpl-preview-panel">
                <div className="p-4 bg-[#111] border border-[#262626] rounded-lg">
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-2">From</p>
                  <p className="text-sm text-white">{preview.dealershipName}</p>
                </div>
                <div className="p-6 bg-white rounded-lg overflow-hidden">
                  {form.logo_url && (
                    <div className="text-center mb-4">
                      <img src={form.logo_url} alt="Logo" className="max-h-20 inline-block" />
                    </div>
                  )}
                  <div
                    className="template-preview text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none break-words [&>p]:mb-3 [&>ul]:mb-3 [&>ul]:ml-5 [&>ul>li]:mb-1 [&>p:last-child]:mb-0"
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                    ref={el => { if (el) el.querySelectorAll('td').forEach(td => td.style.display = 'table-cell'); }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.body) }}
                    data-testid="tpl-preview-body"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <p className="text-[10px] text-slate-500">Preview uses sample data. Actual emails will use real lead data.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(false)}
                    className="border-[#333] text-xs font-bold tracking-wider uppercase text-slate-400"
                    data-testid="tpl-back-to-editor-btn"
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Back to Editor
                  </Button>
                </div>
              </div>
            ) : (
              /* ==================== EDITOR MODE ==================== */
              <form onSubmit={handleSubmit} className="space-y-4 mt-4" data-testid="tpl-editor-form">
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Dealership</Label>
                  <Select value={form.dealership_id} onValueChange={v => setForm(p => ({ ...p, dealership_id: v }))}>
                    <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11" data-testid="tpl-dealership-select">
                      <SelectValue placeholder="Select dealership" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#171717] border-[#262626]">
                      {dealerships.map(d => (
                        <SelectItem key={d.dealership_id} value={d.dealership_id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Alias selector for VinSolutions dealerships */}
                {(() => {
                  const selDlr = dealerships.find(d => d.dealership_id === form.dealership_id);
                  const aliases = selDlr?.email_aliases || [];
                  if (aliases.length === 0) return null;
                  return (
                    <div>
                      <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Alias / Sub-Dealership</Label>
                      <Select value={form.alias_email || ''} onValueChange={v => {
                        const aliasVal = v === '__none__' ? '' : v;
                        // Auto-set website_override from the alias's website
                        const alias = aliases.find(a => a.alias === aliasVal);
                        const aliasWebsite = alias?.website ? alias.website.replace('https://','').replace('http://','') : '';
                        setForm(p => ({ ...p, alias_email: aliasVal, website_override: aliasWebsite || '' }));
                      }}>
                        <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm" data-testid="tpl-alias-select">
                          <SelectValue placeholder="All aliases (shared)" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#171717] border-[#262626]">
                          <SelectItem value="__none__" className="text-slate-400 hover:bg-[#262626]">All aliases (shared)</SelectItem>
                          {aliases.map((a, idx) => {
                            const website = a.website ? a.website.replace('https://','').replace('http://','').replace('www.','') : '';
                            return (
                              <SelectItem key={a.alias || idx} value={a.alias} className="text-white hover:bg-[#262626]">
                                {website || a.alias}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Template Name</Label>
                    <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="New Vehicle Welcome" className="mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600" data-testid="tpl-name-input" required />
                  </div>
                  <div>
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Lead Type</Label>
                    <Select value={form.lead_type} onValueChange={v => setForm(p => ({ ...p, lead_type: v }))}>
                      <SelectTrigger className="mt-1.5 bg-[#111] border-[#333] h-11" data-testid="tpl-lead-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#171717] border-[#262626]">
                        {Object.entries(LEAD_TYPE_LABELS).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Logo Library */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase flex items-center gap-2">
                      <Image className="w-3 h-3" /> Logo
                    </Label>
                    <button
                      type="button"
                      onClick={() => { setShowLogoLibrary(!showLogoLibrary); if (!showLogoLibrary) fetchLogoLibrary(); }}
                      className="text-[10px] font-bold tracking-wider text-slate-500 hover:text-red-400 uppercase transition-colors flex items-center gap-1"
                      data-testid="tpl-logo-library-toggle"
                    >
                      <ImagePlus className="w-3 h-3" /> {showLogoLibrary ? 'Hide Library' : 'Logo Library'}
                    </button>
                  </div>

                  {/* Selected logo preview */}
                  {form.logo_url && !showLogoLibrary && (
                    <div className="mt-2 flex items-center gap-3 p-3 bg-[#111] border border-[#262626] rounded-lg">
                      <img src={form.logo_url} alt="Selected logo" className="h-12 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                      <span className="text-xs text-slate-400 flex-1 truncate">{form.logo_url.split('/').pop()}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setForm(p => ({ ...p, logo_url: '' }))} className="text-slate-500 hover:text-red-400 h-8 px-2" data-testid="tpl-logo-clear">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}

                  {/* Logo library grid */}
                  {showLogoLibrary && (
                    <div className="mt-2 p-4 bg-[#111] border border-[#262626] rounded-lg space-y-3" data-testid="tpl-logo-library">
                      <div className="grid grid-cols-5 gap-3">
                        {logoLibrary.map(logo => (
                          <div
                            key={logo.logo_id}
                            className={`relative group cursor-pointer rounded-lg border-2 p-2 flex items-center justify-center h-20 transition-all ${form.logo_url === logo.url ? 'border-red-500 bg-red-500/10' : 'border-[#333] bg-[#0A0A0A] hover:border-[#555]'}`}
                            onClick={() => setForm(p => ({ ...p, logo_url: logo.url }))}
                            data-testid={`logo-item-${logo.logo_id}`}
                          >
                            <img src={logo.url} alt={logo.original_name} className="max-h-14 max-w-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                            {form.logo_url === logo.url && (
                              <div className="absolute top-1 right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center">
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleDeleteLogo(logo.logo_id); }}
                              className="absolute top-1 left-1 w-5 h-5 bg-black/70 rounded-full items-center justify-center hidden group-hover:flex hover:bg-red-600 transition-colors"
                              data-testid={`logo-delete-${logo.logo_id}`}
                            >
                              <X className="w-3 h-3 text-white" />
                            </button>
                          </div>
                        ))}
                        {/* Upload new logo card */}
                        <label className={`cursor-pointer rounded-lg border-2 border-dashed border-[#333] hover:border-red-600/40 p-2 flex flex-col items-center justify-center h-20 transition-all ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                          {uploading ? (
                            <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <>
                              <Upload className="w-5 h-5 text-slate-500 mb-1" />
                              <span className="text-[9px] font-bold tracking-wider text-slate-500 uppercase">Upload</span>
                            </>
                          )}
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                            className="hidden"
                            data-testid="tpl-logo-upload"
                            onChange={(e) => { handleLogoUpload(e.target.files?.[0]); e.target.value = ''; }}
                          />
                        </label>
                      </div>
                      {logoLibrary.length === 0 && (
                        <p className="text-[10px] text-slate-500 text-center py-2">No logos yet. Upload your first one above.</p>
                      )}
                    </div>
                  )}

                  {!form.logo_url && !showLogoLibrary && (
                    <button
                      type="button"
                      onClick={() => { setShowLogoLibrary(true); fetchLogoLibrary(); }}
                      className="mt-1.5 w-full p-3 border border-dashed border-[#333] rounded-lg text-slate-500 hover:text-red-400 hover:border-red-600/30 transition-all text-xs font-bold tracking-wider uppercase flex items-center justify-center gap-2"
                      data-testid="tpl-add-logo-btn"
                    >
                      <ImagePlus className="w-4 h-4" /> Select or Upload Logo
                    </button>
                  )}
                </div>

                {/* Subject Line */}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Subject Line</Label>
                    {isAutoSubjectCRM() && (
                      <span className="text-[10px] text-orange-400 font-medium">Auto-generated for {getSelectedCRMLabel()}</span>
                    )}
                  </div>
                  <Input
                    value={isAutoSubjectCRM() ? 'Re: [Original Subject]' : form.subject}
                    onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                    placeholder="Thank you for your interest in the {{vehicle_year}} {{vehicle_make}} {{vehicle_model}}!"
                    className={`mt-1.5 bg-[#111] border-[#333] h-11 text-sm placeholder:text-slate-600 ${isAutoSubjectCRM() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="tpl-subject-input"
                    disabled={isAutoSubjectCRM()}
                    required={!isAutoSubjectCRM()}
                  />
                  {isAutoSubjectCRM() && (
                    <p className="text-[10px] text-slate-500 mt-1">For {getSelectedCRMLabel()}, subject lines are automatically set to "Re:" + original email subject</p>
                  )}
                </div>

                {/* Website Override */}
                {(() => {
                  const dlr = dealerships.find(d => d.dealership_id === form.dealership_id);
                  const brandWebsites = dlr?.brand_websites || {};
                  const mainWebsite = dlr?.website || '';
                  const aliases = dlr?.email_aliases || [];
                  const hasOptions = Object.keys(brandWebsites).some(k => brandWebsites[k]) || aliases.length > 0;
                  if (!hasOptions && !mainWebsite) return null;
                  // Build alias website options
                  const aliasOptions = aliases
                    .filter(a => a.website)
                    .map(a => ({
                      label: a.website.replace('https://','').replace('http://','').replace('www.',''),
                      value: a.website.replace('https://','').replace('http://',''),
                    }));
                  return (
                    <div>
                      <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-1 block">Website Source</Label>
                      <Select value={form.website_override || ''} onValueChange={v => setForm(p => ({ ...p, website_override: v === 'default' ? '' : v }))}>
                        <SelectTrigger className="bg-[#111] border-[#333] h-10 text-sm" data-testid="tpl-website-override">
                          <SelectValue placeholder="Default (main website)" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#171717] border-[#262626]">
                          <SelectItem value="default">{mainWebsite ? `Default (${mainWebsite})` : 'Default (main website)'}</SelectItem>
                          {aliasOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                          {Object.entries(brandWebsites).filter(([_, url]) => url).map(([brand, url]) => (
                            <SelectItem key={brand} value={url}>{brand} ({url})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-slate-500 mt-1">Which website to use for inventory lookups and vehicle links.</p>
                    </div>
                  );
                })()}

                {/* Email Buttons */}
                {form.lead_type !== 'service_appointment' && form.lead_type !== 'onstar_service' && (
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-3 block">Email Buttons</Label>
                  <div className="grid grid-cols-2 gap-3 p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg">
                    {/* New vehicle buttons */}
                    {form.lead_type !== 'used_vehicle_inquiry' && form.lead_type !== 'used_vehicle_inquiry_cpo' && (<>
                    {form.lead_type !== 'new_vehicle_inquiry' && (
                    <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                      <span className="text-xs text-slate-300">Window Sticker</span>
                      <Switch
                        checked={form.show_window_sticker}
                        onCheckedChange={v => setForm(p => ({ ...p, show_window_sticker: v }))}
                        data-testid="tpl-toggle-window-sticker"
                      />
                    </div>
                    )}
                    <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                      <span className="text-xs text-slate-300">New Vehicle Warranty</span>
                      <Switch
                        checked={form.show_warranty_btn}
                        onCheckedChange={v => setForm(p => ({ ...p, show_warranty_btn: v }))}
                        data-testid="tpl-toggle-warranty"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                      <span className="text-xs text-slate-300">Schedule Test Drive</span>
                      <Switch
                        checked={form.show_test_drive_btn || false}
                        onCheckedChange={v => setForm(p => ({ ...p, show_test_drive_btn: v }))}
                        data-testid="tpl-toggle-test-drive"
                      />
                    </div>
                    {form.lead_type === 'new_vehicle_inquiry' && (
                    <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                      <span className="text-xs text-slate-300">New Vehicle Inventory</span>
                      <Switch
                        checked={form.show_new_inventory_btn || false}
                        onCheckedChange={v => setForm(p => ({ ...p, show_new_inventory_btn: v }))}
                        data-testid="tpl-toggle-new-inventory"
                      />
                    </div>
                    )}
                    <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                      <span className="text-xs text-slate-300">Pre-Owned Vehicles</span>
                      <Switch
                        checked={form.show_preowned_btn}
                        onCheckedChange={v => setForm(p => ({ ...p, show_preowned_btn: v }))}
                        data-testid="tpl-toggle-preowned"
                      />
                    </div>
                    </>)}
                    {/* Used vehicle / CPO buttons */}
                    {(form.lead_type === 'used_vehicle_inquiry' || form.lead_type === 'used_vehicle_inquiry_cpo') && (<>
                    <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                      <span className="text-xs text-slate-300">CPO Benefits</span>
                      <Switch
                        checked={form.show_cpo_benefits}
                        onCheckedChange={v => setForm(p => ({ ...p, show_cpo_benefits: v }))}
                        data-testid="tpl-toggle-cpo-benefits"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                      <span className="text-xs text-slate-300">CPO vs. Used</span>
                      <Switch
                        checked={form.show_cpo_vs_used}
                        onCheckedChange={v => setForm(p => ({ ...p, show_cpo_vs_used: v }))}
                        data-testid="tpl-toggle-cpo-vs-used"
                      />
                    </div>
                    <div className="flex items-center justify-between p-2 rounded bg-[#111] border border-[#262626]">
                      <span className="text-xs text-slate-300">CPO Inspection Form</span>
                      <Switch
                        checked={form.show_cpo_inspection}
                        onCheckedChange={v => setForm(p => ({ ...p, show_cpo_inspection: v }))}
                        data-testid="tpl-toggle-cpo-inspection"
                      />
                    </div>
                    </>)}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Use {`{{email_buttons}}`} merge field to place buttons anywhere, or they auto-append at the bottom.</p>
                </div>
                )}

                {/* Template Body - Rich Text Editor */}
                <div>
                  <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase mb-2 block">Template Body</Label>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex flex-wrap gap-1.5 flex-1 p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded-lg mr-3">
                      <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mr-2 self-center">Insert:</span>
                      {TEMPLATE_VARS.map(v => (
                        <button key={v.key} type="button" onClick={() => insertVariable(v.key)} className="px-2 py-1 text-[10px] font-bold tracking-wider bg-[#1A1A1A] hover:bg-red-600/20 text-slate-400 hover:text-red-400 border border-[#333] hover:border-red-600/30 rounded transition-all" data-testid={`insert-var-${v.key.replace(/[{}]/g, '')}`}>
                          {v.label}
                        </button>
                      ))}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const quill = quillRef.current?.getEditor?.();
                          if (quill) lastCursorPos.current = quill.getSelection();
                          setShowImageInsert(true); setImageInsertForm({ url: '', linkUrl: '', altText: '', width: '' });
                        }}
                        className="border-[#333] text-[10px] font-bold tracking-wider uppercase text-slate-400 hover:bg-emerald-600/20 hover:text-emerald-400 hover:border-emerald-600/30"
                        data-testid="tpl-insert-image-btn"
                      >
                        <Film className="w-3.5 h-3.5 mr-1" /> Image / GIF
                      </Button>
                    </div>
                  </div>
                  <div className="quill-dark-wrapper">
                    <ReactQuill
                      ref={quillRef}
                      value={form.body}
                      onChange={v => setForm(p => ({ ...p, body: v }))}
                      modules={quillModulesWithHandler}
                      formats={quillFormats}
                      placeholder="Write your email template here..."
                      data-testid="tpl-body-editor"
                    />
                  </div>
                </div>

                <Button type="submit" disabled={saving || !form.dealership_id} className="w-full bg-red-600 hover:bg-red-700 text-white font-bold text-xs tracking-widest uppercase h-11" data-testid="tpl-submit-btn">
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : editing ? 'Update Template' : 'Create Template'}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Image/GIF Insert Dialog */}
        <Dialog open={showImageInsert} onOpenChange={setShowImageInsert}>
          <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-lg" data-testid="image-insert-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg font-semibold text-white uppercase tracking-wider flex items-center gap-2">
                <Film className="w-5 h-5 text-emerald-500" /> Insert Image / GIF
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {/* Upload or URL */}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Image Source</Label>
                <div className="mt-2 flex gap-2">
                  <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-dashed border-[#333] rounded-lg text-slate-400 hover:text-emerald-400 hover:border-emerald-600/30 cursor-pointer transition-all text-xs font-bold tracking-wider uppercase">
                    {uploadingImage ? (
                      <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><Upload className="w-4 h-4" /> Upload File</>
                    )}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      className="hidden"
                      data-testid="image-insert-upload"
                      onChange={(e) => { handleImageUploadForBody(e.target.files?.[0]); e.target.value = ''; }}
                    />
                  </label>
                </div>
                <div className="mt-2">
                  <Label className="text-[10px] font-bold tracking-widest text-slate-600 uppercase">Or paste image URL</Label>
                  <Input
                    value={imageInsertForm.url}
                    onChange={e => setImageInsertForm(p => ({ ...p, url: e.target.value }))}
                    placeholder="https://example.com/image.gif"
                    className="mt-1 bg-[#111] border-[#333] h-10 text-sm placeholder:text-slate-600"
                    data-testid="image-insert-url"
                  />
                </div>
              </div>

              {/* Preview */}
              {imageInsertForm.url && (
                <div className="p-3 bg-[#111] border border-[#222] rounded-lg text-center">
                  <img
                    src={imageInsertForm.url}
                    alt="Preview"
                    className="max-h-40 mx-auto rounded"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <p className="text-[10px] text-slate-500 mt-2 truncate">{imageInsertForm.url}</p>
                </div>
              )}

              {/* Link URL (optional) */}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase flex items-center gap-1.5">
                  <Link2 className="w-3.5 h-3.5" /> Link URL <span className="text-slate-600 normal-case font-normal">(optional — makes image clickable)</span>
                </Label>
                <Input
                  value={imageInsertForm.linkUrl}
                  onChange={e => setImageInsertForm(p => ({ ...p, linkUrl: e.target.value }))}
                  placeholder="https://example.com/destination-page"
                  className="mt-1.5 bg-[#111] border-[#333] h-10 text-sm placeholder:text-slate-600"
                  data-testid="image-insert-link-url"
                />
              </div>

              {/* Alt Text */}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Alt Text</Label>
                <Input
                  value={imageInsertForm.altText}
                  onChange={e => setImageInsertForm(p => ({ ...p, altText: e.target.value }))}
                  placeholder="Cadillac vehicles showcase"
                  className="mt-1.5 bg-[#111] border-[#333] h-10 text-sm placeholder:text-slate-600"
                  data-testid="image-insert-alt"
                />
              </div>

              {/* Width */}
              <div>
                <Label className="text-xs font-bold tracking-widest text-slate-500 uppercase">Max Width <span className="text-slate-600 normal-case font-normal">(optional, e.g. 400px or 80%)</span></Label>
                <Input
                  value={imageInsertForm.width}
                  onChange={e => setImageInsertForm(p => ({ ...p, width: e.target.value }))}
                  placeholder="100%"
                  className="mt-1.5 bg-[#111] border-[#333] h-10 text-sm placeholder:text-slate-600"
                  data-testid="image-insert-width"
                />
              </div>

              <Button
                type="button"
                onClick={insertImageIntoBody}
                disabled={!imageInsertForm.url}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs tracking-widest uppercase h-11"
                data-testid="image-insert-confirm-btn"
              >
                Insert Image
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Lead Type Filter Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-[#0A0A0A] border border-[#262626] p-1 h-auto flex flex-col items-center gap-1">
          <div className="flex flex-wrap justify-center">
            <TabsTrigger value="all" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-400 text-xs font-bold tracking-wider uppercase px-4 py-2">
              All
            </TabsTrigger>
            {['new_vehicle_inquiry', 'new_vehicle_inquiry_vin', 'used_vehicle_inquiry', 'used_vehicle_inquiry_cpo', 'general_inquiry'].map(k => (
              <TabsTrigger key={k} value={k} className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-400 text-xs font-bold tracking-wider uppercase px-4 py-2">
                {LEAD_TYPE_LABELS[k]}
              </TabsTrigger>
            ))}
          </div>
          <div className="flex flex-wrap justify-center">
            {['service_appointment', 'onstar_service', 'parts_inquiry'].map(k => (
              <TabsTrigger key={k} value={k} className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-slate-400 text-xs font-bold tracking-wider uppercase px-4 py-2">
                {LEAD_TYPE_LABELS[k]}
              </TabsTrigger>
            ))}
          </div>
        </TabsList>
      </Tabs>

      {/* Dealerships with Templates */}
      {dealerships.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-[#262626] p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <h3 className="font-heading text-xl font-semibold text-white uppercase mb-2">No Dealerships Yet</h3>
          <p className="text-slate-500 text-sm">Add a dealership first to create templates.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          {[...dealerships].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((dlr) => {
            const dlrTemplates = getTemplatesForDealership(dlr.dealership_id);
            const filteredTemplates = getFilteredTemplates(dlrTemplates);
            const isExpanded = expandedDealership === dlr.dealership_id;

            return (
              <Card
                key={dlr.dealership_id}
                className={`bg-[#0A0A0A] border-[#262626] overflow-hidden transition-all ${isExpanded ? 'border-red-600/30' : 'hover:border-[#404040]'}`}
                data-testid={`dealership-templates-${dlr.dealership_id}`}
              >
                <button
                  onClick={() => { setExpandedDealership(isExpanded ? null : dlr.dealership_id); setTemplateAliasFilter('all'); }}
                  className="w-full p-5 flex items-center justify-between text-left hover:bg-[#111] transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-red-600/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-white uppercase tracking-wider">{dlr.name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {dlrTemplates.length} template{dlrTemplates.length !== 1 ? 's' : ''}
                        {activeTab !== 'all' && filteredTemplates.length !== dlrTemplates.length && (
                          <span className="text-slate-600"> ({filteredTemplates.length} {LEAD_TYPE_LABELS[activeTab]})</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {dlrTemplates.length === 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); seedDefaults(dlr.dealership_id); }}
                        disabled={seeding === dlr.dealership_id}
                        className="border-[#333] bg-[#111] hover:bg-[#1a1a1a] text-white text-[10px] font-bold tracking-wider uppercase"
                      >
                        {seeding === dlr.dealership_id ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <><Wand2 className="w-3 h-3 mr-1" /> Seed Defaults</>
                        )}
                      </Button>
                    )}
                    {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-[#262626] p-5 bg-[#0F0F0F]">
                    {/* Filters row */}
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                    {/* Alias filter for VinSolutions dealerships */}
                    {(dlr.email_aliases || []).length > 0 && (
                        <Select value={templateAliasFilter} onValueChange={setTemplateAliasFilter}>
                          <SelectTrigger className="bg-[#111] border-[#333] h-8 text-xs w-[220px]" data-testid="tpl-alias-filter">
                            <SelectValue placeholder="All aliases" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#171717] border-[#262626]">
                            <SelectItem value="all" className="text-white hover:bg-[#262626]">All Aliases</SelectItem>
                            <SelectItem value="__shared__" className="text-slate-400 hover:bg-[#262626]">Shared (no alias)</SelectItem>
                            {(dlr.email_aliases || []).map((a, idx) => {
                              const website = a.website ? a.website.replace('https://','').replace('http://','').replace('www.','') : '';
                              return (
                                <SelectItem key={a.alias || idx} value={a.alias} className="text-white hover:bg-[#262626]">
                                  {website || a.alias}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                    )}
                      <button
                        onClick={() => { const next = !showDeleted; setShowDeleted(next); fetchTemplates(next); }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-[10px] font-bold tracking-wider uppercase border transition-colors ${showDeleted ? 'bg-red-600/10 text-red-400 border-red-500/30' : 'bg-[#111] text-slate-500 border-[#333] hover:border-slate-500'}`}
                        data-testid="show-deleted-toggle"
                      >
                        <Trash2 className="w-3 h-3" />
                        {showDeleted ? 'Hide Deleted' : 'Show Deleted'}
                      </button>
                    </div>
                    {(() => {
                      let displayTemplates = filteredTemplates;
                      if ((dlr.email_aliases || []).length > 0 && templateAliasFilter !== 'all') {
                        displayTemplates = filteredTemplates.filter(t =>
                          templateAliasFilter === '__shared__' ? !t.alias_email : (t.alias_email || '') === templateAliasFilter
                        );
                      }
                      return displayTemplates.length === 0 ? (
                      <div className="text-center py-8">
                        <FileText className="w-8 h-8 mx-auto mb-2 text-slate-600" />
                        <p className="text-slate-500 text-sm mb-4">
                          {dlrTemplates.length === 0 ? 'No templates yet' : `No ${LEAD_TYPE_LABELS[activeTab]} templates`}
                        </p>
                        <Button onClick={() => openNew(dlr.dealership_id)} variant="outline" size="sm" className="border-[#333] bg-[#111] hover:bg-[#1a1a1a] text-white text-xs font-bold tracking-wider uppercase">
                          <Plus className="w-3 h-3 mr-1" /> Create Template
                        </Button>
                      </div>
                    ) : (
                      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={displayTemplates.map(t => t.template_id)} strategy={rectSortingStrategy}>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {displayTemplates.map((tpl) => (
                              <SortableTemplateCard
                                key={tpl.template_id}
                                tpl={tpl}
                                onPreview={setQuickPreview}
                                onEdit={handleEdit}
                                onDuplicate={handleDuplicate}
                                onDelete={handleDelete}
                                onRestore={handleRestore}
                                onPermanentDelete={handlePermanentDelete}
                                LEAD_TYPE_COLORS={LEAD_TYPE_COLORS}
                                LEAD_TYPE_LABELS={LEAD_TYPE_LABELS}
                              />
                            ))}
                            <Card className="bg-[#111] border-[#262626] border-dashed hover:border-red-600/30 transition-all cursor-pointer" onClick={() => openNew(dlr.dealership_id)}>
                              <div className="p-4 flex flex-col items-center justify-center min-h-[140px] text-slate-500 hover:text-red-400 transition-colors">
                                <Plus className="w-8 h-8 mb-2" />
                                <span className="text-xs font-bold tracking-wider uppercase">Add Template</span>
                              </div>
                            </Card>
                          </div>
                        </SortableContext>
                      </DndContext>
                    );
                    })()}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Quick Preview Dialog */}
      <Dialog open={!!quickPreview} onOpenChange={(open) => { if (!open) setQuickPreview(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="quick-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase tracking-wider">
              {quickPreview?.name}
            </DialogTitle>
          </DialogHeader>
          {quickPreview && (() => {
            const qp = renderQuickPreviewData(quickPreview);
            return (
              <div className="mt-2 space-y-4">
                <div className="p-4 bg-[#111] border border-[#262626] rounded-lg">
                  <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mb-1">Subject</p>
                  <p className="text-sm text-white">{qp.subject || '(Auto-generated)'}</p>
                </div>
                <div className="p-6 bg-white rounded-lg overflow-hidden">
                  {quickPreview.logo_url && (
                    <div className="text-center mb-4">
                      <img src={quickPreview.logo_url} alt="Logo" className="max-h-20 inline-block" onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  )}
                  <div
                    className="template-preview text-sm text-gray-800 leading-relaxed prose prose-sm max-w-none break-words [&>p]:mb-3 [&>ul]:mb-3 [&>ul]:ml-5 [&>ul>li]:mb-1 [&>p:last-child]:mb-0"
                    style={{ overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                    ref={el => { if (el) el.querySelectorAll('td').forEach(td => td.style.display = 'table-cell'); }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(qp.body) }}
                    data-testid="quick-preview-body"
                  />
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${LEAD_TYPE_COLORS[quickPreview.lead_type]}`}>
                      {LEAD_TYPE_LABELS[quickPreview.lead_type]}
                    </Badge>
                    <span className="text-[10px] text-slate-500">{qp.dealershipName}</span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => { handleEdit(quickPreview); setQuickPreview(null); }}
                    className="border-[#333] text-xs font-bold tracking-wider uppercase text-slate-400"
                  >
                    <Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit Template
                  </Button>
                </div>
                <p className="text-[10px] text-slate-500">Preview uses sample data. Actual emails will use real lead data.</p>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
